import React, { useState } from "react";
import {
  Database,
  Globe,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  RefreshCw,
  Search,
  Filter,
  Plus,
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { AIAgent } from "./types";
import { useNavigate } from "@tanstack/react-router";

interface DataSource {
  id: string;
  name: string;
  description: string;
  type: "database" | "api" | "file" | "stream" | "cache";
  status: "connected" | "disconnected" | "error" | "syncing";
  lastSync: Date;
  size: string;
  records: number;
  agents: string[];
  url?: string;
  credentials: "configured" | "missing" | "expired";
  databaseType?:
    | "postgresql"
    | "mysql"
    | "redis"
    | "mongodb"
    | "elasticsearch"
    | "snowflake"
    | "kafka"
    | "rabbitmq"
    | "influxdb"
    | "s3"
    | "gcs"
    | "azure";
}

// Utility to format time difference
function formatRelativeTime(date: Date, now: Date) {
  const diff = now.getTime() - date.getTime();
  const absDiff = Math.abs(diff);

  if (absDiff < 60 * 1000) return "Real-time";
  if (absDiff < 60 * 60 * 1000) {
    const mins = Math.round(absDiff / (60 * 1000));
    return `${mins} minute${mins > 1 ? "s" : ""} ago`;
  }
  if (absDiff < 24 * 60 * 60 * 1000) {
    const hours = Math.round(absDiff / (60 * 60 * 1000));
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  }
  const days = Math.round(absDiff / (24 * 60 * 60 * 1000));
  return `${days} day${days > 1 ? "s" : ""} ago`;
}

const now = new Date();
const minutesAgo = (n: number) => new Date(now.getTime() - n * 60 * 1000);

// Sample data sources
const sampleDataSources: DataSource[] = [
  {
    id: "1",
    name: "Customer Database",
    description: "Primary customer data for support bot training and testing",
    type: "database",
    status: "connected",
    lastSync: minutesAgo(5),
    size: "2.3 GB",
    records: 15420,
    agents: ["Beauty Sales Agent", "Recommendation Engine"],
    url: "postgresql://customer-db.agenttest.com",
    credentials: "configured",
    databaseType: "postgresql",
  },
  {
    id: "2",
    name: "Product Catalog API",
    description: "Real-time product information and inventory data",
    type: "api",
    status: "connected",
    lastSync: minutesAgo(1),
    size: "156 MB",
    records: 2340,
    agents: ["Recommendation Engine", "Data Analysis Agent"],
    url: "https://api.products.agenttest.com",
    credentials: "configured",
  },
  {
    id: "3",
    name: "Document Storage",
    description: "File storage for documents and media files",
    type: "file",
    status: "connected",
    lastSync: minutesAgo(5),
    size: "234.5 GB",
    records: 45678,
    agents: ["File Agent", "Content Moderation Bot"],
    credentials: "configured",
    databaseType: "s3",
  },
  {
    id: "4",
    name: "Analytics Database",
    description: "User behavior and analytics data",
    type: "database",
    status: "connected",
    lastSync: minutesAgo(10),
    size: "1.8 GB",
    records: 8920,
    agents: ["Data Analysis Agent"],
    url: "postgresql://analytics-db.agenttest.com",
    credentials: "configured",
    databaseType: "postgresql",
  },
  {
    id: "5",
    name: "Knowledge Base",
    description: "Company knowledge base and documentation",
    type: "file",
    status: "connected",
    lastSync: minutesAgo(30),
    size: "45.2 GB",
    records: 12340,
    agents: ["Beauty Sales Agent"],
    credentials: "configured",
    databaseType: "s3",
  },
];

const DataSourceRow: React.FC<{
  dataSource: DataSource;
}> = ({ dataSource }) => {
  const navigate = useNavigate();
  const now = new Date();

  const getStatusColor = (status: string) => {
    switch (status) {
      case "connected":
        return "bg-pink-900/30 text-pink-400 border-pink-600/30";
      case "disconnected":
        return "bg-gray-900/30 text-gray-400 border-gray-600/30";
      case "error":
        return "bg-red-900/30 text-red-400 border-red-600/30";
      case "syncing":
        return "bg-blue-900/30 text-blue-400 border-blue-600/30";
      default:
        return "bg-gray-900/30 text-gray-400 border-gray-600/30";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "connected":
        return <CheckCircle className="h-4 w-4" />;
      case "disconnected":
        return <XCircle className="h-4 w-4" />;
      case "error":
        return <AlertTriangle className="h-4 w-4" />;
      case "syncing":
        return <RefreshCw className="h-4 w-4 animate-spin" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "database":
        return <Database className="h-6 w-6 text-white" />;
      case "api":
        return <Globe className="h-6 w-6 text-white" />;
      case "file":
        return <FileText className="h-6 w-6 text-white" />;
      default:
        return <Database className="h-6 w-6 text-white" />;
    }
  };

  const getDatabaseTypeIcon = (databaseType?: string) => {
    switch (databaseType) {
      case "postgresql":
        return (
          <img src="/postgresql.svg" alt="PostgreSQL" className="h-6 w-6" />
        );
      case "redis":
        return <img src="/redis.svg" alt="Redis" className="h-6 w-6" />;
      case "mongodb":
        return <img src="/mongodb.svg" alt="MongoDB" className="h-6 w-6" />;
      case "elasticsearch":
        return (
          <img
            src="/elasticsearch.svg"
            alt="Elasticsearch"
            className="h-6 w-6"
          />
        );
      case "snowflake":
        return (
          <img src="/snowflake-color.svg" alt="Snowflake" className="h-6 w-6" />
        );
      case "s3":
        return <img src="/s3.svg" alt="S3" className="h-6 w-6" />;
      default:
        return getTypeIcon(dataSource.type);
    }
  };

  return (
    <div
      className="flex items-center justify-between p-4 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-750 transition-colors cursor-pointer"
      onClick={() =>
        navigate({
          to: "/data-sources/$dataSourceId",
          params: { dataSourceId: dataSource.id },
        })
      }
    >
      <div className="flex items-center space-x-4 flex-1">
        {/* Database Type Icon */}
        <div className="flex items-center justify-center w-8 h-8 bg-gray-700 rounded-lg">
          {getDatabaseTypeIcon(dataSource.databaseType)}
        </div>

        {/* Name and Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <h3 className="text-sm font-medium text-gray-100 truncate">
              {dataSource.name}
            </h3>
          </div>
          <p className="text-xs text-gray-400 mt-1">{dataSource.description}</p>
        </div>

        {/* Records and Size */}
        <div className="flex items-center space-x-2 text-xs text-gray-400">
          <span>{dataSource.size}</span>
          <span>•</span>
          <span>{dataSource.records.toLocaleString()} records</span>
        </div>

        {/* Last Sync */}
        <div className="text-xs text-gray-400 min-w-0">
          {formatRelativeTime(dataSource.lastSync, now)}
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2">
          <Button
            size="sm"
            variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
            onClick={(e) => {
              e.stopPropagation();
              // Handle refresh action
            }}
          >
            <RefreshCw className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const KnowledgeTab: React.FC<{ agent: AIAgent }> = ({ agent }) => {
  const [dataSources] = useState<DataSource[]>(sampleDataSources);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  // Filter to show only data sources that are already used by this agent
  const agentDataSources = dataSources.filter((dataSource) =>
    dataSource.agents.includes(agent.name),
  );

  const handleImportNew = () => {
    // TODO: Open import modal or navigate to data sources page
    console.log("Import new data source");
  };

  // Filter data sources (only show agent's data sources)
  const filteredDataSources = agentDataSources.filter((dataSource) => {
    const matchesStatus =
      statusFilter === "all" || dataSource.status === statusFilter;
    const matchesType = typeFilter === "all" || dataSource.type === typeFilter;
    const matchesSearch =
      searchQuery === "" ||
      dataSource.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dataSource.description.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesStatus && matchesType && matchesSearch;
  });

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Filters */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-center space-y-3 lg:space-y-0 lg:space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-300">Filters:</span>
            </div>

            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1 bg-gray-700 border border-gray-600 rounded-md text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                <option value="all">All Status</option>
                <option value="connected">Connected</option>
                <option value="disconnected">Disconnected</option>
                <option value="error">Error</option>
                <option value="syncing">Syncing</option>
              </select>

              {/* Type Filter */}
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-3 py-1 bg-gray-700 border border-gray-600 rounded-md text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                <option value="all">All Types</option>
                <option value="database">Database</option>
                <option value="api">API</option>
                <option value="file">File</option>
                <option value="stream">Stream</option>
                <option value="cache">Cache</option>
              </select>
            </div>

            <span className="text-sm text-gray-400">
              {filteredDataSources.length} of {agentDataSources.length} sources
            </span>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 lg:ml-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search data sources..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-1 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-gray-100 placeholder-gray-400"
                />
              </div>
              <Button
                className="flex items-center justify-center space-x-2 bg-pink-400 hover:bg-pink-500"
                onClick={handleImportNew}
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Import New Source</span>
                <span className="sm:hidden">Import</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Sources List */}
      <div className="space-y-2">
        {filteredDataSources.length > 0 ? (
          filteredDataSources.map((dataSource) => (
            <DataSourceRow key={dataSource.id} dataSource={dataSource} />
          ))
        ) : (
          <div className="text-center py-8 text-gray-400">
            <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No data sources found matching your criteria</p>
          </div>
        )}
      </div>
    </div>
  );
};
